/**
 * Shared pieces of the documentation pages (/docs and in-app Help), set in
 * the Carbon type of mockup carbon/01 (DESIGN.md "Typography"):
 *   DocsPage      the public docs container: breadcrumb, then the page, at
 *                 Carbon's content padding.
 *   DocsHeader    the page's one h1 (Headline Small, 300) over its lede.
 *   DocSection    an h2 section (Heading, 1.25rem/400) 48px below the last.
 *   DocNote       a neutral aside on layer-01: an info or check glyph, an
 *                 optional 600 title and the text. Never a hue: tips, notes
 *                 and "this is normal" confirmations are all neutral (the
 *                 Neutral Bad State Rule); blocking errors are not docs copy.
 *   DocList       a bulleted or numbered list in secondary text.
 *   CheckList     a to-do list drawn with empty square marks (decorative).
 * Wide content (tables, commands) scrolls inside its own box, never the page.
 */
import type { ReactNode } from 'react'
import clsx from 'clsx'
import { Breadcrumb, IconCheck, IconInfo, type BreadcrumbItem } from '@/components/carbon'

export function DocsPage({ crumbs, children }: { crumbs: BreadcrumbItem[]; children: ReactNode }) {
  return (
    <div className="mx-auto max-w-[1584px] px-8 pb-16 pt-6 max-c-md:px-4 max-c-md:pb-12 max-c-md:pt-4">
      <Breadcrumb items={crumbs} />
      {children}
    </div>
  )
}

export function DocsHeader({ title, lede, meta }: { title: ReactNode; lede?: ReactNode; meta?: ReactNode }) {
  return (
    <header className="mt-8 max-w-[56rem]">
      <h1 className="m-0 max-w-[24ch] text-balance text-headline-sm font-light tracking-normal text-text-primary max-c-md:text-[2rem]">
        {title}
      </h1>
      {lede ? <p className="m-0 mt-4 max-w-[56ch] text-heading font-light text-text-secondary max-c-md:text-[1.125rem]">{lede}</p> : null}
      {meta ? <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-helper text-text-helper">{meta}</div> : null}
    </header>
  )
}

export function DocSection({ title, id, children, className }: { title: ReactNode; id?: string; children: ReactNode; className?: string }) {
  return (
    <section aria-labelledby={id} className={clsx('mt-12 max-w-[56rem]', className)}>
      <h2 id={id} className="m-0 mb-4 scroll-mt-6 text-heading font-normal text-text-primary">
        {title}
      </h2>
      <div className="flex flex-col gap-4 text-body text-text-secondary">{children}</div>
    </section>
  )
}

export function DocSubsection({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="mt-4 flex flex-col gap-4">
      <h3 className="m-0 text-body-lg font-semibold text-text-primary">{title}</h3>
      {children}
    </div>
  )
}

export function DocNote({ title, children, tone = 'info', className }: { title?: ReactNode; children?: ReactNode; tone?: 'info' | 'check'; className?: string }) {
  const Icon = tone === 'check' ? IconCheck : IconInfo
  return (
    <aside className={clsx('flex gap-3 bg-layer-1 px-4 py-3.5 text-body text-text-secondary', className)}>
      <Icon className="mt-0.5 text-[color:var(--icon-secondary)]" />
      <div className="min-w-0">
        {title ? <p className="m-0 font-semibold text-text-primary">{title}</p> : null}
        {children ? <div className={clsx('flex flex-col gap-2', title && 'mt-1')}>{children}</div> : null}
      </div>
    </aside>
  )
}

export function DocList({ items, ordered, className }: { items: ReactNode[]; ordered?: boolean; className?: string }) {
  const Tag = ordered ? 'ol' : 'ul'
  return (
    <Tag className={clsx('m-0 flex flex-col gap-1.5 pl-5 text-body text-text-secondary marker:text-text-helper', ordered ? 'list-decimal' : 'list-disc', className)}>
      {items.map((item, i) => (
        <li key={i} className="pl-1">
          {item}
        </li>
      ))}
    </Tag>
  )
}

export function CheckList({ items }: { items: string[] }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0 text-body text-text-secondary">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none ring-1 ring-inset ring-line-strong" />
          <span className="min-w-0">{item}</span>
        </li>
      ))}
    </ul>
  )
}

/** Inline emphasis in docs copy: 600 in primary ink. */
export function Strong({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-text-primary">{children}</strong>
}
